import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useTheme } from 'styled-components';
import {
  blockContinuationPageCount,
  blockCustomExpandsByRowSlice,
  blockGridColumnWidthsState,
  blockIsVirtualizedSlice,
  selectedBlocksState,
} from 'venn-state';
import type { GridSettings } from 'venn-components';
import { BlockContainer } from '../../../../../venn-components/src/blocks/BlockContainer';
import { BlockContext } from '../../../../../venn-components/src/blocks/contexts/block-context';
import {
  calcGridItemWidth,
  ExpandActionButton,
  useContinuationTrailerMeasurement,
  useExpandBlock,
} from 'venn-components';

export interface ContinuationBlockWrapperProps {
  sourceBlockId: string;
  continuationIndex: number;
  /** Rendered-list position — used for page-number context passed to BlockContext. */
  pageNumber: number;
  gridSettings: GridSettings;
  sourceBlockWidthInColumns: number;
  inPrintMode: boolean;
  sliceHeightPx: number;
  scrollOffsetPx: number;
  sliceStartRowIndex: number;
  sliceDataHeightPx: number;
}

/**
 * Renders a single continuation slice of an expanded block.
 *
 * Most block types clip the source block's AG-Grid output to the correct data-row window via
 * `overflow: clip` — deliberately not a scroll container, so focus cannot shift the slice — and
 * counter-translate the sticky `.ag-header` back to `y = 0` of the viewport using a CSS custom
 * property on the inner wrapper. Virtualized-slice block types (`isVirtualizedSliceBlockType`,
 * e.g. Tax Impact) instead get a fixed-height, row-virtualized AG-Grid window via `sliceWindow` on
 * BlockContext and need neither the clip nor the counter-translate, since AG-Grid's own header
 * already sits at the top of that window. Print export always falls back to the clip path so PDF
 * output stays byte-identical.
 */
const ContinuationBlockWrapper = ({
  sourceBlockId,
  continuationIndex,
  pageNumber,
  gridSettings,
  sourceBlockWidthInColumns,
  inPrintMode,
  sliceHeightPx,
  scrollOffsetPx,
  sliceStartRowIndex,
  sliceDataHeightPx,
}: ContinuationBlockWrapperProps) => {
  const totalContinuations = useRecoilValue(blockContinuationPageCount(sourceBlockId));
  const customExpandsByRowSlice = useRecoilValue(blockCustomExpandsByRowSlice(sourceBlockId));
  const isVirtualizedSliceBlock = useRecoilValue(blockIsVirtualizedSlice(sourceBlockId));
  const sharedColumnWidths = useRecoilValue(blockGridColumnWidthsState(sourceBlockId));
  const selectedBlocks = useRecoilValue(selectedBlocksState);
  const isSelected = selectedBlocks.includes(sourceBlockId);

  const isLast = continuationIndex === totalContinuations - 1;

  // The last slice keeps the CSS-clip path. Its data window is shorter than a full slice because it
  // also carries the below-data tail, so AG-Grid's scroll range ends before the slice's first row can
  // reach the top of such a short viewport — it clamps, silently dropping the final row. Every other
  // slice has at least a full slice of content below its start, so it can always scroll into position.
  const sliceWindow = useMemo(
    () =>
      isVirtualizedSliceBlock && !inPrintMode && !isLast
        ? {
            dataHeightPx: sliceDataHeightPx,
            startRowIndex: sliceStartRowIndex,
            columnWidths: isEmpty(sharedColumnWidths) ? undefined : sharedColumnWidths,
          }
        : undefined,
    [isVirtualizedSliceBlock, inPrintMode, isLast, sliceDataHeightPx, sliceStartRowIndex, sharedColumnWidths],
  );

  // A windowed slice never mounts the trailer, so its own measurement would read a false "no trailer"
  // height; disabling it keeps the shared-by-blockId state authoritative only from instances that
  // actually render the full trailer.
  const { blockRef, setTrailerElement } = useContinuationTrailerMeasurement(sourceBlockId, {
    enabled: !sliceWindow,
  });
  const blockContextValue = useMemo(
    () => ({
      blockRef,
      setTrailerElement,
      pageNumber,
      isContinuation: true as const,
      continuationIndex,
      sliceWindow,
    }),
    [blockRef, setTrailerElement, pageNumber, continuationIndex, sliceWindow],
  );
  const { contract } = useExpandBlock(sourceBlockId);

  const { Colors } = useTheme();
  const borderColor = isSelected ? Colors.DeepDarkBlue : Colors.GreyScale.Grey30;

  const blockWidthPx = calcGridItemWidth(gridSettings, sourceBlockWidthInColumns);

  const borderStyle = {
    width: blockWidthPx,
    borderLeft: `2px solid ${borderColor}`,
    borderRight: `2px solid ${borderColor}`,
    borderTop: `2px dashed ${borderColor}`,
    borderBottom: `2px ${isLast ? 'solid' : 'dashed'} ${borderColor}`,
  };

  const contractButton = !inPrintMode && (
    <ExpandActionButton placement="top" icon="arrow-up-to-dotted-line" label="Contract" onClick={contract} />
  );

  // Both self-slicing paths render only their own slice's content, so neither needs the clip window
  // or the header counter-translate that the CSS-clipped path depends on.
  if (customExpandsByRowSlice || sliceWindow) {
    return (
      <div className="relative">
        <div ref={blockRef} style={borderStyle}>
          <BlockContext.Provider value={blockContextValue}>
            <BlockContainer id={sourceBlockId} index={0} containerWidth={blockWidthPx} />
          </BlockContext.Provider>
        </div>
        {contractButton}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="overflow-clip" style={{ ...borderStyle, height: sliceHeightPx }}>
        <div
          ref={blockRef}
          className="[&_.ag-header]:relative [&_.ag-header]:z-[5] [&_.ag-header]:[background:var(--header-bg)] [&_.ag-header]:[transform:translateY(var(--header-translate))]"
          style={
            {
              transform: `translateY(-${scrollOffsetPx}px)`,
              '--header-translate': `${scrollOffsetPx}px`,
              '--header-bg': Colors.White,
            } as React.CSSProperties
          }
        >
          <BlockContext.Provider value={blockContextValue}>
            <BlockContainer id={sourceBlockId} index={0} containerWidth={blockWidthPx} />
          </BlockContext.Provider>
        </div>
      </div>
      {contractButton}
    </div>
  );
};

export default ContinuationBlockWrapper;
